import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client"; 
import PromptLabInterface from "@/components/prompt-lab/PromptLabInterface";
import PromptLabSidebar from "@/components/prompt-lab/PromptLabSidebar";
import { Button } from "@/components/ui/button";
import { MessageSquare, AlertCircle, Search } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";

const PromptLabPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  const promptIdParam = searchParams.get('prompt');
  const searchQueryParam = searchParams.get('search') || '';
  
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(promptIdParam);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [apiKeyConfigured, setApiKeyConfigured] = useState<boolean>(true);
  const [globalSearchQuery, setGlobalSearchQuery] = useState<string>(searchQueryParam);
  
  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to use PromptLab",
        variant: "destructive"
      });
      navigate('/sign-in');
      return;
    }
    
    // Check if API key is configured
    const checkApiKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("openai-chat", {
          body: { userMessage: "test" }
        });
        
        if (error) {
          console.error("Error checking API key:", error);
          if (error.message && error.message.includes("API key not configured")) {
            setApiKeyConfigured(false);
          }
        }
      } catch (error) {
        console.error("Error checking API key:", error);
      }
    };
    
    checkApiKey();
    
    // Set loading to false after a short delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [user, navigate, toast]);
  
  useEffect(() => {
    // If we have a prompt ID from URL params, select it
    if (promptIdParam) {
      setSelectedPrompt(promptIdParam);
    }
  }, [promptIdParam]);
  
  const handlePromptSelect = (promptId: string) => {
    setSelectedPrompt(promptId);
    // Update URL params
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('prompt', promptId);
      return newParams;
    });
  };
  
  const handleClearPrompt = () => {
    setSelectedPrompt(null);
    // Update URL params
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('prompt');
      return newParams;
    });
  };
  
  const handleGlobalSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Update URL params with search query
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (globalSearchQuery) {
        newParams.set('search', globalSearchQuery);
      } else {
        newParams.delete('search');
      }
      return newParams;
    });
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }
  
  // Redirect to sign-in if no user
  if (!user) {
    return null; // Prevent flash before redirect
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          <header className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center">
                <MessageSquare className="mr-2 h-8 w-8 text-primary" />
                PromptLab
              </h1>
              <p className="text-muted-foreground mt-1">
                Test prompts from your collections in a real chat environment
              </p>
            </div>
            
            <div className="flex items-center gap-4 w-full md:w-auto">
              <form onSubmit={handleGlobalSearch} className="relative flex-1 md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search all prompts..."
                  className="pl-8 w-full"
                  value={globalSearchQuery}
                  onChange={(e) => setGlobalSearchQuery(e.target.value)}
                />
              </form>
              
              {selectedPrompt && (
                <Button
                  variant="outline"
                  className="flex items-center gap-1 whitespace-nowrap"
                  onClick={handleClearPrompt}
                >
                  <MessageSquare className="h-4 w-4" />
                  Free Chat
                </Button>
              )}
            </div>
          </header>
          
          {!apiKeyConfigured && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>OpenAI API Key Missing</AlertTitle>
              <AlertDescription>
                The OpenAI API key is not configured. Please set the OPENAI_API_KEY secret in the Supabase Edge Functions settings.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <PromptLabSidebar
                onPromptSelect={handlePromptSelect}
                selectedPrompt={selectedPrompt}
                globalSearchQuery={searchQueryParam}
              />
            </div>
            
            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden h-[800px] flex flex-col">
                <PromptLabInterface
                  promptId={selectedPrompt}
                  modelId="gpt-4o-mini" // Default model, no longer selectable
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PromptLabPage;
